// GA4/GTM conversion event tracking
// All events use a consistent structure
// for clean GA4 reporting and funnel analysis

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[];
    }
}

type ConversionType = 'registration' | 'upgrade';
type PlanKey = 'free' | 'starter' | 'growth';

interface ConversionEventParams {
    conversion_type: ConversionType;
    plan: PlanKey;
    trial: boolean;
    value: number;
    currency: 'USD';
}

// Value map — reflects expected revenue
// Free: $0, Trial: $2 (discounted, 
// unconfirmed revenue), Paid: full MRR
const PLAN_VALUES: Record<string, number> = {
    free: 0,
    starter_trial: 2,
    starter_paid: 9,
    growth: 29,
};

export function trackConversionEvent(
    params: ConversionEventParams
) {
    if (typeof window === 'undefined') return;
    if (!window.dataLayer) window.dataLayer = [];

    // Prevent duplicate registration 
    // events in same session
    if (params.conversion_type === 'registration') {
        const key = `rk_tracked_${params.plan}`;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
    }

    try {
        window.dataLayer.push({
            event: 'account_conversion',
            conversion_type: params.conversion_type,
            plan: params.plan,
            trial: params.trial,
            value: params.value,
            currency: params.currency,
        });
    } catch {
        // Silently fail — tracking must never
        // break the application
    }
}

// Named helpers for each scenario:

export const trackFreeRegistration = () =>
    trackConversionEvent({
        conversion_type: 'registration',
        plan: 'free',
        trial: false,
        value: PLAN_VALUES.free,
        currency: 'USD',
    });

export const trackStarterRegistration = (hasTrial: boolean) =>
    trackConversionEvent({
        conversion_type: 'registration',
        plan: 'starter',
        trial: hasTrial,
        value: hasTrial
            ? PLAN_VALUES.starter_trial
            : PLAN_VALUES.starter_paid,
        currency: 'USD',
    });

export const trackGrowthRegistration = () =>
    trackConversionEvent({
        conversion_type: 'registration',
        plan: 'growth',
        trial: false,
        value: PLAN_VALUES.growth,
        currency: 'USD',
    });

export const trackStarterUpgrade = (hasTrial: boolean) =>
    trackConversionEvent({
        conversion_type: 'upgrade',
        plan: 'starter',
        trial: hasTrial,
        value: hasTrial
            ? PLAN_VALUES.starter_trial
            : PLAN_VALUES.starter_paid,
        currency: 'USD',
    });

export const trackGrowthUpgrade = () =>
    trackConversionEvent({
        conversion_type: 'upgrade',
        plan: 'growth',
        trial: false,
        value: PLAN_VALUES.growth,
        currency: 'USD',
    });
